import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ScaleService {
  private readonly basePlanningSheetWidth = 2180; // Base width for 30 days
  private readonly planningSheetPadding = 96;
  private planningSheetWidth: number; // Dynamic width based on the month days

  // Planning sheet zoom, applied on top of the fit-to-screen scale. 1 = fit (minimum).
  readonly zoomLevels: number[] = [1, 1.25, 1.5, 1.75, 2]; // labels shown to the user
  // how strongly a level magnifies: applied = 1 + (level - 1) * intensity -> 200% label = 1.5x actual
  private readonly zoomIntensity = 0.5;
  private zoomSubject = new BehaviorSubject<number>(1);
  readonly zoom$ = this.zoomSubject.asObservable();

  constructor() {
    this.planningSheetWidth = this.basePlanningSheetWidth; // Initialize with base width
  }

  /**
   * Returns the number of days in a given month and year.
   * @param month - The month (1 = January, 12 = December).
   * @param year - The year (e.g., 2024).
   * @returns The number of days in the given month.
   */
  private getDaysInMonth(month: number, year: number): number {
    return new Date(year, month, 0).getDate(); // Returns the last date of the month, which gives the total days
  }

  private setPlanningSheetWidthProperty(): void {
    document.documentElement.style.setProperty('--planning-sheet-width', `${this.planningSheetWidth}px`);
  }

  /**
   * Adjusts the planning sheet width based on the number of days in the month.
   * @param month - The month (1 = January, 12 = December).
   * @param year - The year (e.g., 2024).
   */
  public updatePlanningSheetWidth(month: number, year: number): void {
    const daysInMonth = this.getDaysInMonth(month, year);

    // Adjust planning sheet width based on days in the month
    this.planningSheetWidth = this.basePlanningSheetWidth + this.getWidthAdjustment(daysInMonth);

    // Update both scale and dimension properties
    this.updateScale(window.innerWidth, window.innerHeight);
    this.setPlanningSheetWidthProperty();
  }

  /**
   * Adjusts the width in pixels based on the number of days in the month.
   * @param daysInMonth - The number of days in the month.
   * @returns The width adjustment in pixels.
   */
  private getWidthAdjustment(daysInMonth: number): number {
    const emToPx = (em: number) => em * 16; // Convert em to pixels assuming 1em = 16px

    switch (daysInMonth) {
      case 28:
        return -emToPx(6);  // Subtract 6em for 28 days
      case 29:
        return -emToPx(3);  // Subtract 3em for 29 days
      case 31:
        return emToPx(3);   // Add 3em for 31 days
      default:
        return 0;           // No adjustment for 30 days
    }
  }

  get zoom(): number {
    return this.zoomSubject.value;
  }

  /** Sets the zoom to the nearest allowed level and updates the --planning-zoom CSS variable. */
  public setZoom(level: number): void {
    const nearest = this.zoomLevels.reduce((a, b) => (Math.abs(b - level) < Math.abs(a - level) ? b : a));
    if (nearest === this.zoom && document.documentElement.style.getPropertyValue('--planning-zoom')) return;
    if (this.zoom === this.zoomLevels[0] && nearest > this.zoom) {
      this.captureFitColumnWidth(); // leaving fit: remember the day column width so zoom can't squeeze the days
    }
    const applied = 1 + (nearest - 1) * this.zoomIntensity;
    document.documentElement.style.setProperty('--planning-zoom', `${applied}`);
    // zoomed: right sidebar (Soll / Ist / Saldo / Ferien / DAG) becomes sticky again (see styles.scss)
    document.documentElement.classList.toggle('orgApp-planning-zoomed', applied > 1);
    this.zoomSubject.next(nearest);
    this.updateScale(window.innerWidth, window.innerHeight);
    // let Mobiscroll re-measure the timeline after the layout width changed
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  public zoomIn(): void {
    const next = this.zoomLevels.find((l) => l > this.zoom);
    if (next !== undefined) this.setZoom(next);
  }

  public zoomOut(): void {
    const prev = [...this.zoomLevels].reverse().find((l) => l < this.zoom);
    if (prev !== undefined) this.setZoom(prev);
  }

  /**
   * Mobiscroll stretches day columns to fill the timeline. Store the fit-view column width (layout px)
   * so CSS can use it as min-width; when zoomed, days then overflow and scroll instead of shrinking.
   */
  private captureFitColumnWidth(): void {
    const col = document.querySelector('.orgApp-planning-table .mbsc-timeline-column') as HTMLElement | null;
    if (col?.offsetWidth) {
      document.documentElement.style.setProperty('--planning-day-col-width', `${col.offsetWidth}px`);
    }
  }

  /** Back to fit-to-screen. */
  public resetZoom(): void {
    this.setZoom(this.zoomLevels[0]);
  }

  /**
   * Calculates the scaling factor based on the current width.
   * @param currentWidth - The current viewport or container width.
   * @returns The calculated scaling factor.
   */
  private calculateScaleFactor(currentWidth: number): number {
    const usableWidth = currentWidth - this.planningSheetPadding;
    const percentageDifference = (this.planningSheetWidth - usableWidth) / usableWidth;
    return 1 / (1 + percentageDifference);
  }

  /**
   * Sets the scaling factor as a CSS variable based on the current width.
   * @param currentWidth - The current viewport width.
   */
  public updateScale(currentWidth: number, currentHeigh: number): void {
    const scaleFactor = this.calculateScaleFactor(currentWidth);
    const zoom = this.getAppliedZoom();
    document.documentElement.style.setProperty('--scale-factor', `${scaleFactor * zoom}`); // fit x zoom
    const heightAdjustment = 324; // The amount to subtract from viewport height
    const newHeight = currentHeigh - heightAdjustment;
    const addedScaleHeight = newHeight * scaleFactor 
    const scaledHeight = newHeight + addedScaleHeight;
     // Set the scaled height based on the original height and scale factor (divided by zoom so the visible height stays the same)
     document.documentElement.style.setProperty('--scaled-timeline-height', `${scaledHeight / zoom}px`);
  }

  /**
   * Zoom currently applied on the page. Read from the CSS variable (single source of truth) so every
   * ScaleService instance (root + any module-level provider) scales with the same zoom.
   */
  private getAppliedZoom(): number {
    const value = parseFloat(document.documentElement.style.getPropertyValue('--planning-zoom'));
    return value > 0 ? value : 1;
  }
}